// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Qui a le droit de voir quoi, en matière d'abonnement et de licence.
//
// ⚠️ POURQUOI CE FICHIER EXISTE. La règle « cet écran n'est pas pour lui »
// vivait à DEUX endroits qui ne se parlaient pas : `Settings.js` masquait
// l'onglet « Abonnement », `Sidebar.js` ne masquait rien du tout. Le menu
// proposait donc une entrée qui menait à une page repliée sur « Profil » — un
// cul-de-sac silencieux, sans erreur, pour tout siège de licence.
//
// Une règle d'accès écrite deux fois finit toujours par diverger. Celle-ci
// n'est écrite qu'ici, et les deux écrans l'appellent.
//
// ⚠️ CE N'EST PAS UN CONTRÔLE DE SÉCURITÉ. Masquer une entrée de menu n'empêche
// personne d'appeler la route. Les gardes qui comptent sont côté serveur
// (`@require_auth`, `license_role`, `_etat_droits_compte`) ; ici on décide
// seulement de ce qu'on MONTRE, pour ne pas proposer une impasse.

/**
 * Le compte gère-t-il lui-même un abonnement payant ?
 *
 * Trois situations, et la deuxième est celle qui a manqué :
 *
 * - **abonné individuel** (Essentiel, Pro, ou aucun forfait encore) → oui.
 *   Aucune licence : l'onglet est son seul moyen de souscrire ou de résilier.
 *
 * - **titulaire Cabinet** → oui, ET C'EST LE CAS QUI AVAIT ÉTÉ MANQUÉ. Cabinet
 *   ouvre une licence, si bien que le test « appartient à une licence » le
 *   confondait avec un salarié. Il payait 279 € par mois sans pouvoir résilier,
 *   changer de palier, ni revenir sur une rétrogradation.
 *
 * - **siège de licence** (collègue d'un Cabinet, salarié d'un client
 *   Entreprise) → non. Il n'a pas d'abonnement à lui ; lui en montrer un
 *   l'inviterait à payer une seconde fois ce que son employeur paie déjà.
 *
 * ⚠️ Le discriminant est `est_titulaire_abonnement`, calculé par le serveur
 * (`core/quotas._get_license_info`) en comparant l'abonnement Stripe du compte
 * à celui de la licence. Ni le `license_role` — administrer des sièges n'est
 * pas détenir la carte bancaire — ni la simple présence d'une licence.
 */
export const gereSonAbonnement = (user) => {
  if (!user) return false;
  const appartientAUneLicence = !!(user.license_id || user.license);
  if (!appartientAUneLicence) return true;
  return !!user.est_titulaire_abonnement;
};

/**
 * Faut-il conduire ce compte vers le choix d'un forfait à la connexion ?
 *
 * ⚠️ CE TEST ÉTAIT ÉCRIT TROIS FOIS dans `Login.js` (mot de passe, MFA, CGU) et
 * il était faux les trois fois : il ne regardait que `tier_code`. Or un siège de
 * licence n'a AUCUN `pricing_tier_id` — il puise dans le pool de son
 * employeur (`blueprints/enterprise.py`, acceptation d'invitation). Tout membre
 * d'une licence était donc envoyé à chaque connexion vers `?tab=billing`, un
 * onglet qui lui est masqué : la page se repliait sur « Profil », et il ne
 * voyait jamais son tableau de bord en arrivant. Depuis toujours, pour tous les
 * clients Entreprise.
 *
 * La question n'est pas « a-t-il un forfait ? » mais « doit-il en choisir un ? ».
 * Un siège de licence, non : quelqu'un paie déjà pour lui.
 */
export const doitChoisirUnForfait = (user) => {
  if (!user) return false;
  if (['admin', 'dev'].includes(user.role)) return false;  // comptes internes
  if (!gereSonAbonnement(user)) return false;              // un siège ne choisit rien
  return !user.tier_code;
};

/**
 * Le compte administre-t-il une licence (espace client) ?
 *
 * ⚠️ ÉCRITE ICI parce qu'elle l'était DEUX fois : `Sidebar.js` masquait
 * l'entrée de menu, et `App.js` ne gardait PAS la route `/license-portal` —
 * seule page sensible sans garde, quand tous les écrans `/admin/*` passent par
 * `ProtectedRoute`. Un simple membre qui connaissait l'adresse — ou qui y
 * revenait par le bouton « page précédente » — ouvrait l'écran d'administration
 * de sa licence (constaté au test de Marc du 22/09/2026).
 *
 * ⚠️ RAPPEL : ce n'est PAS le contrôle de sécurité. Les routes serveur passent
 * toutes par `_require_license_admin()` et répondent 403 — c'est vérifié, et
 * c'est ce qui explique que l'écran se vide au second passage. Ici on évite
 * d'AFFICHER un écran qui n'appartient pas à ce compte : le mal n'est pas la
 * fuite de données (il n'y en a pas), c'est l'écran resté à l'image du compte
 * PRÉCÉDENT après un changement de compte, que le cache aller-retour du
 * navigateur restitue tel quel, sans rejouer le moindre appel.
 */
export const administreUneLicence = (user) => {
  if (!user) return false;
  if (user.is_active === false) return false;
  const appartientAUneLicence = !!(user.license_id || user.license);
  return appartientAUneLicence && user.license_role === 'license_admin';
};

/**
 * Le compte voit-il la page « Vérifier une certification » (chantier ⑩) ?
 *
 * À partir de Pro (décision de Marc, 21/09/2026) : Pro, Cabinet, tout siège de
 * licence, et les comptes Evalens. ⛔ Ni Essentiel, ni l'essai.
 *
 * ⚠️ Pendant d'affichage de `core/droits.peut_verifier_certification`, qui est la
 * SERRURE (403). Deux différences assumées : l'écran ne connaît ni l'état de la
 * licence ni le statut de l'abonnement — un siège de licence fermée ne peut plus
 * se connecter, et une résiliation efface `tier_code`.
 */
export const PALIERS_VERIFICATION_CERTIFICATION = ['PRO', 'CABINET'];

export const peutVerifierCertification = (user) => {
  if (!user || user.is_active === false) return false;
  if (['admin', 'dev'].includes(user.role)) return true;
  if (user.license_id || user.license) return true;
  return PALIERS_VERIFICATION_CERTIFICATION.includes(user.tier_code);
};

/**
 * Les types de diagnostic (normes) que ce compte peut lancer (chantier T2).
 *
 * Pendant d'affichage de `core/droits.normes_autorisees`, qui est la SERRURE
 * (403 `NORME_NON_ACTIVEE` au dépôt). Le serveur envoie la liste à la connexion
 * et sur `/api/auth/me` : `normes_actives`, des CODES — le libellé est la clé
 * `norme_<code>` du module.
 *
 * Rend `null` quand la liste est INCONNUE (session ouverte avant la mise en
 * production, profil pas encore rafraîchi) : l'écran ne bloque alors rien et
 * laisse le serveur décider. Bloquer sur une information absente, ce serait
 * couper un client pour une raison qui n'existe pas.
 *
 * Le choix n'est MONTRÉ que s'il y en a plusieurs (public « clé en main ») :
 * voir `choixDeNormeAffiche`.
 */
export const normesProposees = (user) => {
  if (!user || !Array.isArray(user.normes_actives)) return null;
  return user.normes_actives;
};

export const choixDeNormeAffiche = (user) => {
  const normes = normesProposees(user);
  return !!normes && normes.length > 1;
};
